import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  addSecondaryUserMutation,
  cancelSecondaryUserInviteMutation,
  disableSecondaryUserMutation,
  getRegistrantAccountOptions,
  getRegistrantAccountUsersOptions,
  reenableSecondaryUserMutation,
  transferPrimaryOwnershipMutation,
  updateRegistrantOrgPaymentAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import { invalidateRegistrantUserQueries } from "@/lib/registrant-user-query-invalidation";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { AddUserDialog } from "./add-user-dialog";
import { ChangeBillingAccountDialog } from "./change-billing-account-dialog";
import { DisableUserDialog } from "./disable-user-dialog";
import { MakePrimaryDialog } from "./make-primary-dialog";
import {
  RegistrantUserActionsMenu,
  getRegistrantUserActions,
} from "./registrant-user-actions";
import { RegistrantUserDetailsSheet } from "./registrant-user-details-sheet";
import { UserManagementHelpButton } from "./user-management-help";
import { UserRolesCell, getUserRoles } from "./user-roles-cell";

// Define accessor keys for registrant account users
const REGISTRANT_USER_ACCESSOR_KEYS = [
  "id",
  "email",
  "name",
  "phone",
  "roles",
  "portals",
  "status",
] as const;

type RegistrantUserAccessorKeys =
  (typeof REGISTRANT_USER_ACCESSOR_KEYS)[number];
type RegistrantUserColumnProperties = ColumnProperties<
  RegistrantAccountUserResponseSchema,
  RegistrantUserAccessorKeys
>;

const getRegistrantUserColumnProperties = (
  accessorKey: RegistrantUserAccessorKeys,
  options: {
    paymentAccountId?: string;
    editable?: boolean;
  },
): RegistrantUserColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        allowBlankFilter: false,
        getValue: (row) => row.id,
      };
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row) => row.email ?? "",
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row) => {
          // Hide name for pending users
          if (row.status === "pending") {
            return "";
          }
          return row.name ?? "";
        },
      };
    case "phone":
      return {
        displayName: "Phone",
        defaultVisible: true,
        getValue: (row) => {
          // Hide phone for pending users
          if (row.status === "pending") {
            return "";
          }
          return row.phone ?? "";
        },
      };
    case "roles":
      return {
        displayName: "Roles",
        defaultVisible: true,
        defaultWidth: 220,
        // Filter and sort on the same text the badges show.
        getValue: (row) =>
          getUserRoles(row, options.paymentAccountId).join(", "),
        renderCell: (row) => (
          <UserRolesCell
            row={row}
            paymentAccountId={options.paymentAccountId}
            editable={options.editable}
          />
        ),
      };
    case "portals":
      return {
        displayName: "Portals",
        defaultVisible: true,
        defaultWidth: 200,
        getValue: (row) => {
          const parts = new Set<string>();
          for (const li of row.login_identities ?? []) {
            if (!li.inspection_only) {
              parts.add("Registration Portal");
            }
            if (li.license_access_mode === "ALL") {
              parts.add("Inspection Portal");
            }
          }
          return [...parts].join(", ");
        },
      };
    case "status":
      return {
        displayName: "Status",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => {
          const status = row.status ?? "active";
          return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
        },
      };

    default: {
      // TypeScript exhaustiveness check
      const _exhaustive: never = accessorKey;
      return _exhaustive;
    }
  }
};

interface DashboardUsersProps {
  isAuthSetupComplete: boolean;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: React.ReactNode;
}

export function DashboardUsers({
  isAuthSetupComplete,
  leadingContent,
}: DashboardUsersProps) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [makePrimaryUserId, setMakePrimaryUserId] = useState<string | null>(
    null,
  );
  const [isMakePrimaryDialogOpen, setIsMakePrimaryDialogOpen] = useState(false);
  const [billingTarget, setBillingTarget] =
    useState<RegistrantAccountUserResponseSchema | null>(null);
  const [disableTarget, setDisableTarget] =
    useState<RegistrantAccountUserResponseSchema | null>(null);
  const queryClient = useQueryClient();

  const { data: accountData } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete,
  });

  const canManageUsers = Boolean(accountData?.is_admin);
  const primaryAccountId = accountData?.primary_id ?? accountData?.id;

  // Fetch registrant account users (primary + secondary accounts)
  const {
    data: usersData,
    isLoading: isAccountLoading,
    error: accountError,
  } = useQuery({
    ...getRegistrantAccountUsersOptions(),
    enabled: isAuthSetupComplete,
  });

  // Extract users list from response
  const usersList = usersData?.users ?? [];
  const paymentAccountId =
    usersData?.payment_account_id ??
    accountData?.payment_account_id ??
    primaryAccountId;
  const currentBillingUser = usersList.find((u) => u.id === paymentAccountId);
  const currentBillingName =
    currentBillingUser?.name || currentBillingUser?.email || undefined;

  // Add user mutation
  const addUserMutation = useMutation({
    ...addSecondaryUserMutation(),
    onSuccess: () => {
      // Invalidate only the users query to refresh data
      queryClient.invalidateQueries({
        queryKey: getRegistrantAccountUsersOptions().queryKey,
      });
    },
  });

  // Cancel invite mutation
  const cancelInviteMutation = useMutation({
    ...cancelSecondaryUserInviteMutation(),
    onSuccess: () => {
      setDisableTarget(null);
      // Invalidate the users query to refresh data
      queryClient.invalidateQueries({
        queryKey: getRegistrantAccountUsersOptions().queryKey,
      });
    },
  });

  // Disable secondary user mutation
  const disableUserMutation = useMutation({
    ...disableSecondaryUserMutation(),
    onSuccess: () => {
      // Invalidate the users query to refresh data
      queryClient.invalidateQueries({
        queryKey: getRegistrantAccountUsersOptions().queryKey,
      });
      toast({
        title: "Success",
        description: "User has been disabled successfully",
      });
    },
  });

  // Reenable secondary user mutation
  const reenableUserMutation = useMutation({
    ...reenableSecondaryUserMutation(),
    onSuccess: () => {
      // Invalidate the users query to refresh data
      queryClient.invalidateQueries({
        queryKey: getRegistrantAccountUsersOptions().queryKey,
      });
      toast({
        title: "Success",
        description: "User has been re-enabled successfully",
      });
    },
  });

  // Transfer primary ownership mutation
  const transferPrimaryMutation = useMutation({
    ...transferPrimaryOwnershipMutation(),
    onSuccess: () => {
      // Also drops cached per-user details.
      invalidateRegistrantUserQueries(queryClient);
      toast({
        title: "Success",
        description: "Primary ownership has been transferred successfully",
      });
      setIsMakePrimaryDialogOpen(false);
      setMakePrimaryUserId(null);
    },
  });

  const updateBillingAccountMutation = useMutation({
    ...updateRegistrantOrgPaymentAccountMutation(),
    onSuccess: () => {
      setBillingTarget(null);
      invalidateRegistrantUserQueries(queryClient);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to change the billing account: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    },
  });

  // Handle add new user
  const handleAddNewUser = useCallback(() => {
    setIsAddUserDialogOpen(true);
    setSuccessMessage(null);
  }, []);

  // Handle submit add user
  const handleAddUserSubmit = useCallback(
    async (email: string) => {
      try {
        await addUserMutation.mutateAsync({
          body: {
            email,
          },
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to add user: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
        throw error;
      }
    },
    [addUserMutation],
  );

  // Handle success after adding user
  const handleAddUserSuccess = useCallback((email: string) => {
    setSuccessMessage(
      `When ${email} next logs in, they will be asked to confirm they want to join your account`,
    );
    // Clear success message after 10 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 10000);
  }, []);

  // Handle cancel invite
  const handleCancelInvite = useCallback(
    async (userId: string, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent row click
      try {
        await cancelInviteMutation.mutateAsync({
          path: {
            user_id: userId,
          },
        });
        toast({
          title: "Success",
          description: "Invite canceled successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to cancel invite: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
    [cancelInviteMutation],
  );

  // Handle disable user
  const handleDisableUser = useCallback(
    (user: RegistrantAccountUserResponseSchema, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent row click
      setDisableTarget(user);
    },
    [],
  );

  const confirmDisableUser = useCallback(async () => {
    if (!disableTarget) return;
    try {
      await disableUserMutation.mutateAsync({
        path: {
          user_id: disableTarget.id,
        },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to disable user: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  }, [disableTarget, disableUserMutation]);

  // Handle reenable user
  const handleReenableUser = useCallback(
    async (userId: string, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent row click
      try {
        await reenableUserMutation.mutateAsync({
          path: {
            user_id: userId,
          },
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to re-enable user: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
    [reenableUserMutation],
  );

  // Handle make primary user
  const handleMakePrimary = useCallback(
    (userId: string, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent row click
      setMakePrimaryUserId(userId);
      setIsMakePrimaryDialogOpen(true);
    },
    [],
  );

  // Handle confirm make primary
  const handleConfirmMakePrimary = useCallback(
    async (moveBillingToNewPrimary: boolean) => {
      if (!makePrimaryUserId) return;

      try {
        await transferPrimaryMutation.mutateAsync({
          path: {
            user_id: makePrimaryUserId,
          },
          body: { move_billing_to_new_primary: moveBillingToNewPrimary },
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to transfer primary ownership: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
    [makePrimaryUserId, transferPrimaryMutation],
  );

  // Handle row click - admins can view/edit any account, including disabled
  const handleRowClick = useCallback(
    (userId: string) => {
      if (!canManageUsers) {
        return;
      }

      const user = usersList.find((u) => u.id === userId);
      if (!user) {
        return;
      }

      // The flags now live only in the sheet, so the primary needs to open it
      // too. Pending invites can still have roles and portal access staged.
      if (
        user.status === "active" ||
        user.status === "disabled" ||
        user.status === "pending"
      ) {
        setSelectedUserId(userId);
        setIsSheetOpen(true);
      }
    },
    [canManageUsers, usersList],
  );

  if (isAccountLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching users..." />
        </div>
      </div>
    );
  }

  if (accountError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <ErrorComponent
          title={getQueryErrorHeading(accountError)}
          message={getQueryErrorMessage(accountError)}
        />
      </div>
    );
  }

  if (!usersData) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <div className="text-center py-8">
          <p className="text-muted-foreground">No account found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0">
      <DashboardTable<
        RegistrantAccountUserResponseSchema,
        RegistrantUserAccessorKeys
      >
        data={usersList}
        leadingContent={
          <div className="pb-4 space-y-4">
            {leadingContent}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4">
                <p className="text-sm">{successMessage}</p>
              </div>
            )}
            {canManageUsers && (
              <div className="flex justify-end items-center gap-2">
                <UserManagementHelpButton />
                <Button onClick={handleAddNewUser}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            )}
          </div>
        }
        onRowClick={handleRowClick}
        accessorKeys={REGISTRANT_USER_ACCESSOR_KEYS}
        getColumnProperties={(key) =>
          getRegistrantUserColumnProperties(key, {
            paymentAccountId,
            editable: canManageUsers,
          })
        }
        getRowId={(row) => row.id}
        tablePersistenceKey="registrant-users"
        initialSortColumn="name"
        initialSortDirection="asc"
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        rowActionsColumnWidth={70}
        renderRowActions={
          canManageUsers
            ? (row) => {
                // No trigger at all when the row has nothing to act on, e.g.
                // an active primary that already holds the billing account.
                if (
                  getRegistrantUserActions({ user: row, paymentAccountId })
                    .length === 0
                ) {
                  return null;
                }
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Actions for ${row.email ?? row.id}`}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RegistrantUserActionsMenu
                        user={row}
                        paymentAccountId={paymentAccountId}
                        onBilling={() => setBillingTarget(row)}
                        onMakePrimary={(e) => handleMakePrimary(row.id, e)}
                        onDisable={(e) => handleDisableUser(row, e)}
                        onReenable={(e) => handleReenableUser(row.id, e)}
                        onCancelInvite={(e) => handleCancelInvite(row.id, e)}
                        busy={{
                          makePrimary: transferPrimaryMutation.isPending,
                          disable: disableUserMutation.isPending,
                          reenable: reenableUserMutation.isPending,
                          cancelInvite: cancelInviteMutation.isPending,
                        }}
                      />
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
            : undefined
        }
      />

      <RegistrantUserDetailsSheet
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        userId={selectedUserId}
        key={selectedUserId}
      />

      <AddUserDialog
        isOpen={isAddUserDialogOpen}
        onOpenChange={setIsAddUserDialogOpen}
        onSubmit={handleAddUserSubmit}
        onSuccess={handleAddUserSuccess}
      />

      {makePrimaryUserId && (
        <MakePrimaryDialog
          isOpen={isMakePrimaryDialogOpen}
          onOpenChange={setIsMakePrimaryDialogOpen}
          onConfirm={handleConfirmMakePrimary}
          userName={
            usersList.find((u) => u.id === makePrimaryUserId)?.name ||
            usersList.find((u) => u.id === makePrimaryUserId)?.email ||
            "this user"
          }
          currentBillingName={currentBillingName}
          isNewPrimaryAlreadyBilling={paymentAccountId === makePrimaryUserId}
          isLoading={transferPrimaryMutation.isPending}
        />
      )}

      {billingTarget && (
        <ChangeBillingAccountDialog
          isOpen
          onOpenChange={(open) => {
            if (!open) {
              setBillingTarget(null);
            }
          }}
          onConfirm={() =>
            updateBillingAccountMutation.mutate({
              body: { payment_account_id: billingTarget.id },
            })
          }
          newBillingName={
            billingTarget.name || billingTarget.email || "this user"
          }
          currentBillingName={currentBillingName}
          isLoading={updateBillingAccountMutation.isPending}
        />
      )}

      <DisableUserDialog
        user={disableTarget}
        users={usersList}
        isLoading={disableUserMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setDisableTarget(null);
        }}
        onConfirm={() => void confirmDisableUser()}
      />
    </div>
  );
}
